import {ChevronDown} from 'lucide-react';
import { useState } from "react";

export default function UKEurope() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const accordionItems = [
    {
      label: "General guidance",
      content: `**Recognition:** Check mutual recognition rules and language requirements.  
**Documents:** Good Standing, transcripts, and employment letters are commonly required.`
    }
  ];

  const timeline = [
    { label: "CBT", description: "Study, book, and pass CBT" },
    { label: "OSCE", description: "Training period, sit OSCE" },
    { label: "NMC", description: "Complete registration, begin onboarding" }
  ];

  const faqItems = [
    {
      q: "Can I start with CBT while preparing OSCE?",
      a: "Yes. Many candidates prepare sequentially; focus on CBT first, then OSCE training."
    },
    {
      q: "Are NHS Trusts available?",
      a: "Yes. We list both NHS Trusts and private employers through verified recruiters."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">UK & Europe</h1>
          <p className="text-xl text-teal-50">
            CBT → OSCE → NMC registration
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-6 py-16 space-y-12">
        {/* Intro Text */}
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-700 leading-relaxed">
            The UK route centers on <strong>CBT → OSCE → NMC registration</strong>. Europe varies by country; confirm local licensing rules.
          </p>
        </div>

        {/* Popular UK Route */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Popular UK route</h2>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">CBT prep and pass</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">OSCE training and exam</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-600 mr-3 mt-1">•</span>
              <span className="text-gray-700">NMC registration and employer onboarding</span>
            </li>
          </ul>
        </div>

        {/* EU Notes Accordion */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">EU notes (country-by-country varies)</h2>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {accordionItems.map((item, index) => (
              <div key={index}>
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{item.label}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 py-4 bg-gray-50 text-gray-700 leading-relaxed whitespace-pre-line">
                    {item.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Typical UK timeline</h2>
          <div className="space-y-6">
            {timeline.map((step, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0 w-32">
                  <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold">
                    {step.label}
                  </span>
                </div>
                <p className="text-gray-700 ml-4">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            {faqItems.map((item, index) => (
              <div key={index}>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{item.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaqIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-gray-50 text-gray-700 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
          <a
            href="/signup"
            className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors"
          >
            Register Free
          </a>
          <a
            href="/resources"
            className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors"
          >
            See Resources
          </a>
        </div>
      </section>
    </div>
  );
}
